/**
 * NotificationCenter Component
 * Dropdown panel showing notifications list
 */

import React, { useEffect, useRef } from 'react';
import { useOpenWithReturn } from '../../hooks/useReturnNavigation';
import {
  MessageCircle,
  Reply,
  AtSign,
  CheckCircle,
  XCircle,
  FileText,
  TrendingUp,
  Bell,
  Trash2,
  CheckCheck,
} from 'lucide-react';
import {
  useNotifications,
  useMarkAsRead,
  useMarkAllAsRead,
  useDeleteNotification,
} from '../../hooks/useNotifications';
import type { Notification, NotificationType } from '../../types/notification';
import { LoadingSpinner } from './LoadingSpinner';
import { EmptyState } from './EmptyState';
import { formatDistanceToNow } from 'date-fns';

export interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
}

// Icon mapping for notification types
const notificationIcons: Record<NotificationType, React.ElementType> = {
  COMMENT: MessageCircle,
  REPLY: Reply,
  MENTION: AtSign,
  APPROVAL_REQUEST: FileText,
  APPROVAL_APPROVED: CheckCircle,
  APPROVAL_REJECTED: XCircle,
  OKR_ASSIGNED: FileText,
  PROGRESS_UPDATE: TrendingUp,
  CHECK_IN_REMINDER: Bell,
  CHECK_IN_SUBMITTED: TrendingUp,
  SYSTEM: Bell,
};

export const NotificationCenter: React.FC<NotificationCenterProps> = ({
  isOpen,
  onClose,
}) => {
  const openWithReturn = useOpenWithReturn();
  const panelRef = useRef<HTMLDivElement>(null);

  // Fetch notifications only when dropdown is open (cost-effective + Optimistic UI)
  // Optimistic UI: Shows cached data immediately while refetching in background
  const { data, isLoading, isFetching } = useNotifications(
    { page: 1, limit: 20 },
    { enabled: isOpen }
  );

  // DEBUG: Uncomment to debug
  // console.log('🔔 NotificationCenter rendered:', { isOpen, isLoading, isFetching, notifications: data?.notifications?.length });

  const markAsReadMutation = useMarkAsRead();
  const markAllAsReadMutation = useMarkAllAsRead();
  const deleteNotificationMutation = useDeleteNotification();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read if unread
    if (!notification.isRead) {
      await markAsReadMutation.mutateAsync(notification.id);
    }

    // Navigate to action URL if provided
    if (notification.actionUrl) {
      // Back on the page it opens returns to where the user was when they clicked.
      openWithReturn(notification.actionUrl);
      onClose();
    }
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsReadMutation.mutateAsync();
    // No need to manually refetch - React Query will invalidate and refetch automatically
  };

  const handleDeleteNotification = async (
    e: React.MouseEvent,
    notificationId: string
  ) => {
    e.stopPropagation(); // Prevent notification click
    await deleteNotificationMutation.mutateAsync(notificationId);
  };

  const notifications = data?.notifications || [];
  const hasData = data !== undefined;

  return (
    <div
      ref={panelRef}
      className="absolute right-0 mt-2 w-96 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
          {/* Show subtle indicator when refetching in background */}
          {isFetching && hasData && (
            <div className="h-2 w-2 rounded-full bg-primary-400 animate-pulse" />
          )}
        </div>
        {notifications.length > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            disabled={markAllAsReadMutation.isPending}
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
          >
            <CheckCheck className="h-4 w-4" />
            Mark all read
          </button>
        )}
      </div>

      {/* Notifications List */}
      <div className="max-h-96 overflow-y-auto">
        {/* Optimistic UI: Only show spinner if loading AND no cached data */}
        {isLoading && !hasData ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="md" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="py-8">
            <EmptyState
              icon={Bell}
              title="No notifications"
              description="You're all caught up!"
            />
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notifications.map((notification) => {
              const Icon = notificationIcons[notification.type as NotificationType] || Bell;
              const isUnread = !notification.isRead;

              return (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`group relative cursor-pointer px-4 py-3 transition-colors hover:bg-gray-50 ${
                    isUnread ? 'bg-blue-50' : ''
                  }`}
                >
                  {/* Unread Indicator */}
                  {isUnread && (
                    <div className="absolute left-2 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-primary-600" />
                  )}

                  <div className="flex gap-3 pl-4">
                    {/* Icon */}
                    <div
                      className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${
                        isUnread ? 'bg-primary-100' : 'bg-gray-100'
                      }`}
                    >
                      <Icon
                        className={`h-5 w-5 ${
                          isUnread ? 'text-primary-600' : 'text-gray-600'
                        }`}
                      />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm ${
                          isUnread ? 'font-semibold text-gray-900' : 'text-gray-700'
                        }`}
                      >
                        {notification.title}
                      </p>
                      <p className="mt-1 text-sm text-gray-600">{notification.message}</p>
                      <p className="mt-1 text-xs text-gray-500">
                        {formatDistanceToNow(new Date(notification.createdAt), {
                          addSuffix: true,
                        })}
                      </p>
                    </div>

                    {/* Delete Button */}
                    <button
                      onClick={(e) => handleDeleteNotification(e, notification.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-400 hover:text-red-600"
                      aria-label="Delete notification"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer - View All (Optional - page doesn't exist yet) */}
      {/* Uncomment when /notifications page is created
      {notifications.length > 0 && (
        <div className="border-t border-gray-200 px-4 py-3">
          <button
            onClick={() => {
              navigate('/notifications');
              onClose();
            }}
            className="w-full text-center text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            View all notifications
          </button>
        </div>
      )}
      */}
    </div>
  );
};
